import React, { useState, useMemo, useEffect } from 'react';
import { Trophy, Flame, Clock, Star, Target, Trash2, Activity, Zap, Dumbbell, Medal, Globe, Calendar, Award, TrendingUp, Edit3, X } from 'lucide-react';
import { ComposedChart, Bar, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';
import MuscleHeatmap from './MuscleHeatmap';
import { showToast } from '../utils/toast';

const BIG_LIFTS = [
    { key: 'squat', label: 'Squat', aliases: ['back squat', 'barbell back squat', 'squat', 'barbell squat', 'front squat'] },
    { key: 'bench', label: 'Bench Press', aliases: ['bench press', 'barbell bench press', 'flat bench press', 'db bench press', 'dumbbell bench press'] },
    { key: 'deadlift', label: 'Deadlift', aliases: ['deadlift', 'barbell deadlift', 'conventional deadlift', 'romanian deadlift', 'rdl'] },
    { key: 'ohp', label: 'Overhead Press', aliases: ['overhead press', 'shoulder press', 'military press', 'barbell overhead press', 'db shoulder press', 'push press'] }
];

export default function Stats({ user, history, prs, deletePR, glassClass, exerciseDatabase, api, token }) {
    const [statsTab, setStatsTab] = useState('personal');
    const [timeFilter, setTimeFilter] = useState('all');
    const [weeklyChartMode, setWeeklyChartMode] = useState('workouts');
    const [leaderboard, setLeaderboard] = useState({ active: false, wods: [], lifts: [] });
    const [fatigueData, setFatigueData] = useState({});
    const [exerciseLogs, setExerciseLogs] = useState([]);
    const [showAllPRs, setShowAllPRs] = useState(false);
    const [runningPlanData, setRunningPlanData] = useState(null);

    // State voor het direct aanpassen of LBS->KG omrekenen van PR's
    const [localPrs, setLocalPrs] = useState(Array.isArray(prs) ? prs : []);
    const [editPrModal, setEditPrModal] = useState({ isOpen: false, exercise: '', weight: '', unit: 'KG', syncLogs: true });

    useEffect(() => {
        setLocalPrs(Array.isArray(prs) ? prs : []);
    }, [prs]);

    const loadExerciseLogs = () => {
        if (api && token) {
            api.fetch('/profile/exercise-logs', {}, token).then(res => {
                if (Array.isArray(res)) setExerciseLogs(res);
            }).catch(() => {});
        }
    };

    useEffect(() => {
        if (api && token) {
            api.fetch('/leaderboard', {}, token).then(res => {
                if (res) {
                    setLeaderboard(res);
                    if (!res.active && statsTab === 'global') setStatsTab('personal');
                }
            }).catch(() => {});

            api.fetch('/profile/fatigue', {}, token).then(res => {
                if (res) setFatigueData(res);
            }).catch(() => {});

            loadExerciseLogs();
            api.fetch('/running/plan', {}, token).then(res => {
                if (res) setRunningPlanData(res);
            }).catch(() => {});
        }
    }, [statsTab, api, token]);

    const handleSavePrEdit = async (e) => {
        e.preventDefault();
        const newW = parseFloat(String(editPrModal.weight).replace(',', '.'));
        if (!newW || newW <= 0) return;

        try {
            await api.fetch('/profile/prs', {
                method: 'PUT',
                body: JSON.stringify({
                    exercise: editPrModal.exercise,
                    weight: newW,
                    unit: editPrModal.unit,
                    syncExerciseLogs: editPrModal.syncLogs
                })
            }, token);

            setLocalPrs(prev => prev.map(p =>
                p.exercise === editPrModal.exercise ? { ...p, weight: newW, unit: editPrModal.unit } : p
            ));
            loadExerciseLogs();
            setEditPrModal({ isOpen: false, exercise: '', weight: '', unit: 'KG', syncLogs: true });
            showToast(`PR voor ${editPrModal.exercise} aangepast naar ${newW} ${editPrModal.unit}!`, 'success');
        } catch (err) {
            showToast('Fout bij aanpassen PR.', 'error');
        }
    };

    const convertPrLbsToKg = () => {
        const currentVal = parseFloat(String(editPrModal.weight).replace(',', '.')) || 0;
        if (currentVal <= 0) return;
        const converted = Math.round((currentVal * 0.45359237) * 2) / 2; // Rond netjes af op 0.5 kg
        setEditPrModal(prev => ({ ...prev, weight: converted, unit: 'KG', syncLogs: true }));
    };

    const groupedLeaderboard = useMemo(() => {
        if (!leaderboard.wods || leaderboard.wods.length === 0) return {};
        const groups = {};
        leaderboard.wods.forEach(score => {
            const lines = score.description.split('\n');
            const titleLine = lines.find(l => l.includes('Titel WOD:') || l.includes('Benchmark:'));
            const resultLine = lines[lines.length - 1];
            if (titleLine && resultLine) {
                const wodName = titleLine.split(':')[1].trim();
                if (!groups[wodName]) groups[wodName] = [];
                if (!groups[wodName].find(s => s.name === score.name && score.name !== 'Anonieme Atleet')) {
                    groups[wodName].push({ name: score.name, result: resultLine.replace(/^(Score|Tijd|Voltooid):\s*/, ''), date: score.date, duration: score.duration });
                }
            }
        });
        Object.keys(groups).forEach(key => {
            const isTime = groups[key].some(s => s.result.includes(':') && !s.result.includes('Rondes'));
            groups[key].sort((a, b) => isTime ? (a.duration || 9999) - (b.duration || 9999) : b.duration - a.duration);
            groups[key] = groups[key].slice(0, 5);
        });
        return groups;
    }, [leaderboard.wods]);

    const groupedLifts = useMemo(() => {
        if (!leaderboard.lifts || leaderboard.lifts.length === 0) return {};
        const groups = {};
        leaderboard.lifts.forEach(score => {
            if (!groups[score.exercise]) groups[score.exercise] = [];
            if (!groups[score.exercise].find(s => s.name === score.name && score.name !== 'Anonieme Atleet')) {
                groups[score.exercise].push(score);
            }
        });
        Object.keys(groups).forEach(key => {
            groups[key].sort((a, b) => b.weight - a.weight);
            groups[key] = groups[key].slice(0, 5);
        });
        return groups;
    }, [leaderboard.lifts]);

    const safeHistory = Array.isArray(history) ? history : [];
    const isFitness = user?.training_type === 'Fitness';

    const getWeekKey = (dateStr) => {
        const d = new Date(dateStr);
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(),0,1));
        return `${d.getUTCFullYear()}-W${Math.ceil((((d - yearStart) / 86400000) + 1)/7)}`;
    };

    const stats = useMemo(() => {
        let allTime = { workoutCount: 0, tonnage: 0, cals: 0, time: 0, amrapChipper: 0 };
        let filtered = { workoutCount: 0, tonnage: 0, cals: 0, time: 0, formatCounts: {}, weeklyData: {} };
        const now = new Date();

        [...safeHistory].reverse().forEach(wod => {
            let wodTime = 0;
            let wodTonnage = 0;
            let wodCals = 0;
            let detectedFormat = 'For Time';

            const desc = wod.description || '';
            const typeStr = String(wod.type || '').toLowerCase();
            const fullText = `${typeStr} ${desc}`.toLowerCase();

            // Begrens individuele workout-tijd op max 240 min (4u) voor realistische statistieken
            if (wod.duration && wod.duration <= 14400) {
                wodTime = Math.round(wod.duration / 60);
            } else {
                const timeMatch = desc.match(/Tijd:\s*(\d+):(\d+)/i);
                if (timeMatch && parseInt(timeMatch[1], 10) <= 240) {
                    wodTime = parseInt(timeMatch[1], 10) + (parseInt(timeMatch[2], 10) / 60);
                } else if (wod.duration > 14400) {
                    wodTime = 60;
                }
            }

            if (fullText.includes('amrap') || fullText.includes('chipper')) {
                detectedFormat = 'AMRAP';
                if (wodTime === 0) {
                    const amrapMatch = fullText.match(/amrap\s*(\d+)/) || fullText.match(/(\d+)\s*minuten\s*amrap/);
                    if (amrapMatch) wodTime = parseInt(amrapMatch[1] || amrapMatch[2], 10);
                }
                allTime.amrapChipper++;
            }
            else if (fullText.includes('emom')) {
                detectedFormat = 'EMOM';
            }
            else if (fullText.includes('tabata')) {
                detectedFormat = 'Tabata';
            }
            else if (typeStr.includes('cardio') || fullText.includes('cardio sessie')) {
                detectedFormat = 'Cardio';
                if (wodTime === 0) wodTime = 30;
            }
            else if (fullText.includes('kracht') || fullText.includes('upper') || fullText.includes('lower') || fullText.includes('full') || fullText.includes('push') || fullText.includes('pull') || fullText.includes('legs') || fullText.includes('deload')) {
                detectedFormat = 'Krachttraining';
                if (wodTime === 0) wodTime = 45;
            }

            let parsedWodData = null;
            if (wod.workout_data) {
                try {
                    parsedWodData = typeof wod.workout_data === 'string' ? JSON.parse(wod.workout_data) : wod.workout_data;
                } catch (e) {}
            }

            if (Array.isArray(parsedWodData) && parsedWodData.length > 0) {
                parsedWodData.forEach(ex => {
                    if (Array.isArray(ex.sets)) {
                        ex.sets.forEach(s => {
                            if (s.done !== false && (parseFloat(s.weight) || 0) > 0 && (parseInt(s.reps, 10) || 0) > 0) {
                                wodTonnage += (parseInt(s.reps, 10) * parseFloat(s.weight));
                            }
                        });
                    }
                });
            } else {
                [...desc.matchAll(/(\d+)x([\d.]+)(kg|lbs)/gi)].forEach(match => { wodTonnage += (parseInt(match[1]) * parseFloat(match[2])); });
                [...desc.matchAll(/(\d+)x([\d-]+)[^(]+\(([\d.]+)(kg|lbs)\)/gi)].forEach(match => {
                    wodTonnage += ((parseInt(match[1]) || 0) * (parseInt(match[2].split('-')[0]) || 0) * (parseFloat(match[4]) || 0));
                });
            }

            const descLines = desc.split('\n');
            descLines.forEach(line => {
                const lLower = line.toLowerCase();
                const numMatch = lLower.match(/(\d+)/);
                const num = numMatch ? parseInt(numMatch[1], 10) : 0;

                if (num > 0) {
                    if (lLower.includes('meter') || lLower.includes('row') || lLower.includes('roeien') || lLower.includes('run') || lLower.includes('hardlopen') || lLower.includes('loopband') || lLower.includes('crosstrainer')) {
                        if (num > 100) wodCals += Math.round(num * 0.08);
                        else wodCals += num * 2;
                    }
                    else if (lLower.includes('cal')) {
                        wodCals += num;
                    }
                    else if (lLower.includes('burpee') || lLower.includes('under') || lLower.includes('jump') || lLower.includes('step') || lLower.includes('touw') || lLower.includes('jack') || lLower.includes('thruster')) {
                        wodCals += Math.round(num * 0.6);
                    }
                }
            });

            if (detectedFormat === 'Cardio' && wodTime > 0) {
                wodCals += Math.round(wodTime * 8);
            }

            allTime.workoutCount++;
            allTime.tonnage += wodTonnage;
            allTime.cals += wodCals;
            allTime.time += wodTime;

            let includeInFilter = true;
            const wodDate = new Date(wod.date);
            const daysDiff = (now - wodDate) / 86400000;

            if (timeFilter === '30' && daysDiff > 30) includeInFilter = false;
            else if (timeFilter === '90' && daysDiff > 90) includeInFilter = false;
            else if (timeFilter === 'year' && wodDate.getFullYear() !== now.getFullYear()) includeInFilter = false;

            if (includeInFilter) {
                filtered.workoutCount++;
                filtered.tonnage += wodTonnage;
                filtered.cals += wodCals;
                filtered.time += wodTime;
                filtered.formatCounts[detectedFormat] = (filtered.formatCounts[detectedFormat] || 0) + 1;

                const weekLabel = getWeekKey(wod.date);
                if (!filtered.weeklyData[weekLabel]) filtered.weeklyData[weekLabel] = { week: weekLabel, Aantal: 0, Tijd: 0, Tonnage: 0 };
                filtered.weeklyData[weekLabel].Aantal += 1;
                filtered.weeklyData[weekLabel].Tijd += wodTime;
                filtered.weeklyData[weekLabel].Tonnage += Math.round(wodTonnage);
            }
        });

        let favFormat = '-';
        const formats = Object.keys(filtered.formatCounts);
        if (formats.length > 0) favFormat = formats.reduce((a, b) => filtered.formatCounts[a] > filtered.formatCounts[b] ? a : b);
        filtered.favFormat = favFormat;

        return { allTime, filtered, heaviestPR: localPrs?.length > 0 ? localPrs.reduce((max, p) => { const w = (p.unit === 'LBS' || p.unit === 'lbs') ? parseFloat(p.weight) * 0.453592 : parseFloat(p.weight); return w > max.w ? {w, weight: p.weight, unit: p.unit || 'kg'} : max; }, {w:0, weight:0, unit:'kg'}) : {weight: 0, unit: 'kg'} };
    }, [safeHistory, localPrs, timeFilter]);

    const chartData = Object.values(stats.filtered.weeklyData).map(item => ({ ...item, Tijd: Math.round(item.Tijd), Tonnage: Math.round(item.Tonnage) }));

    const activeWeeks = [...new Set(safeHistory.map(h => getWeekKey(h.date)))];
    let currentStreak = 0;
    const now = new Date();
    let checkingDate = new Date(now);
    let checkWeekKey = getWeekKey(checkingDate);

    if (!activeWeeks.includes(checkWeekKey)) {
        checkingDate.setDate(checkingDate.getDate() - 7);
        checkWeekKey = getWeekKey(checkingDate);
    }
    while (activeWeeks.includes(checkWeekKey)) {
        currentStreak++;
        checkingDate.setDate(checkingDate.getDate() - 7);
        checkWeekKey = getWeekKey(checkingDate);
    }

    const formatK = (val) => val >= 1000 ? (val / 1000).toFixed(1) + 'k' : Math.round(val);
    const activeWeekCount = Math.max(1, Object.keys(stats.filtered.weeklyData).length);
    const avgSessies = (stats.filtered.workoutCount / activeWeekCount).toFixed(1);

    const strengthScore = Math.min(100, (stats.allTime.tonnage / 50000) * 100) || 10;
    const engineScore = Math.min(100, (stats.allTime.cals / 5000) * 100) || 10;
    const gritScore = Math.min(100, (stats.allTime.amrapChipper / 20) * 100) || 10;
    const disciplineScore = Math.min(100, (currentStreak * 10)) || 10;
    const powerScore = Math.min(100, ((localPrs?.length || 0) / 10) * 100) || 10;
    const capacityScore = Math.min(100, (stats.allTime.workoutCount / 50) * 100) || 10;

    const radarData = isFitness ? [
        { subject: 'Volume', A: Math.round(strengthScore), fullMark: 100 },
        { subject: 'Cardio', A: Math.round(engineScore), fullMark: 100 },
        { subject: 'Capaciteit', A: Math.round(capacityScore), fullMark: 100 },
        { subject: 'Discipline', A: Math.round(disciplineScore), fullMark: 100 },
        { subject: 'Kracht', A: Math.round(powerScore), fullMark: 100 },
    ] : [
        { subject: 'Kracht', A: Math.round(strengthScore), fullMark: 100 },
        { subject: 'Uithouding', A: Math.round(engineScore), fullMark: 100 },
        { subject: 'Grit', A: Math.round(gritScore), fullMark: 100 },
        { subject: 'Discipline', A: Math.round(disciplineScore), fullMark: 100 },
        { subject: 'Power', A: Math.round(powerScore), fullMark: 100 },
    ];

    const coachingTips = isFitness ? {
        'Volume': 'Je totale tonnage kan omhoog. Focus op samengestelde oefeningen (hypertrofie).',
        'Cardio': 'Lekker bezig! Je cardio en metabole conditie zijn uitstekend onderhouden.',
        'Capaciteit': 'Probeer je sessies efficiënter te maken of je frequentie licht te verhogen.',
        'Discipline': 'Consistentie is key voor spieropbouw. Haal wekelijks je doeldagen.',
        'Kracht': 'Test eens in de 4 weken je max op de grote lifts en log een 1RM.'
    } : {
        'Kracht': 'Focus meer op zware, samengestelde lifts (Squat, Deadlift, Bench).',
        'Uithouding': 'Je cardio-engine draait op volle toeren. Prachtig werk!',
        'Grit': 'Daag jezelf uit! Kies vaker voor lange AMRAPs of Chippers (15+ min).',
        'Discipline': 'Consistentie ontbreekt nog. Bouw je "Streak" op.',
        'Power': 'Het ontbreekt aan piek-explosiviteit. Log vaker je Personal Records.'
    };

    const lowestCategory = radarData.reduce((prev, curr) => prev.A < curr.A ? prev : curr);

    const allLoggedEntriesByLift = useMemo(() => {
        const map = {};
        const normalizeName = (raw) => String(raw || '').trim().toLowerCase().replace(/^barbell\s+/i, '');

        // Maak een map van gecorrigeerde PR-plafonds zodat gecorrigeerde LBS->KG waarden ook in de grafiek netjes begrensd zijn
        const prCapByLift = {};
        if (Array.isArray(localPrs)) {
            localPrs.forEach(p => {
                if (p.exercise && parseFloat(p.weight) > 0) {
                    const k = normalizeName(p.exercise);
                    const wKg = (p.unit && p.unit.toUpperCase() === 'LBS') ? parseFloat(p.weight) * 0.453592 : parseFloat(p.weight);
                    prCapByLift[k] = wKg;
                }
            });
        }

        const addPoint = (rawName, dateStr, weightVal, repsVal, rpeVal = null) => {
            let w = parseFloat(weightVal) || 0;
            const r = parseInt(repsVal, 10) || 1;
            if (w <= 0 || !rawName) return;
            const cleanKey = normalizeName(rawName);
            if (!cleanKey || cleanKey.length < 2) return;

            // Als een oude log in LBS stond en > 1.6x boven het huidige PR ligt, reken hem automatisch om naar KG
            if (prCapByLift[cleanKey] && w > prCapByLift[cleanKey] * 1.6) {
                w = Math.round((w * 0.453592) * 2) / 2;
            }

            const e1rm = r === 1 ? w : Math.round(w * (1 + Math.min(r, 20) / 30));
            const dateObj = dateStr ? new Date(dateStr) : new Date();
            if (isNaN(dateObj.getTime())) return;

            if (!map[cleanKey]) {
                map[cleanKey] = { displayName: rawName.trim(), pointsByDay: {}, totalSets: 0 };
            }
            map[cleanKey].totalSets++;

            const dayIso = dateObj.toISOString().slice(0, 10);
            const shortDate = dateObj.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });

            const existing = map[cleanKey].pointsByDay[dayIso];
            if (!existing || e1rm > existing.e1rm) {
                map[cleanKey].pointsByDay[dayIso] = {
                    dayIso,
                    date: shortDate,
                    e1rm: Math.round(e1rm),
                    werkgewicht: Math.round(w * 10) / 10,
                    reps: r,
                    rpe: rpeVal ? Math.round(parseFloat(rpeVal) * 10) / 10 : null,
                    bestSetText: `${r}x ${w}kg`
                };
            }
        };

        if (Array.isArray(exerciseLogs)) {
            exerciseLogs.forEach(log => {
                addPoint(log.exercise_name, log.date, log.actual_weight, log.actual_reps, log.calculated_rpe);
            });
        }

        [...safeHistory].reverse().forEach(wod => {
            let parsedData = null;
            if (wod.workout_data) {
                try { parsedData = typeof wod.workout_data === 'string' ? JSON.parse(wod.workout_data) : wod.workout_data; } catch (e) {}
            }
            if (Array.isArray(parsedData)) {
                parsedData.forEach(ex => {
                    const exName = ex.exercise || ex.name;
                    if (exName && Array.isArray(ex.sets)) {
                        ex.sets.forEach(s => {
                            if (s.done !== false && parseFloat(s.weight) > 0) {
                                addPoint(exName, wod.date, s.weight, s.reps || ex.reps || 1, s.rpe || null);
                            }
                        });
                    }
                });
            }

            const lines = (wod.description || '').split(/\r?\n/);
            lines.forEach(line => {
                const matchInfo = line.match(/^\s*(?:-\s*)?(?:[a-zA-Z]\d?\s*-\s*)?(.*?):\s*(.*)/);
                if (matchInfo) {
                    const exName = matchInfo[1].trim();
                    const setsString = matchInfo[2];
                    setsString.split(',').forEach(set => {
                        const wMatch = set.match(/(\d+)\s*x\s*([\d.]+)\s*(kg|lbs)/i);
                        if (wMatch) {
                            const r = parseInt(wMatch[1], 10) || 1;
                            const w = parseFloat(wMatch[2]);
                            addPoint(exName, wod.date, w, r, null);
                        }
                    });
                }
            });
        });

        if (Array.isArray(localPrs)) {
            localPrs.forEach(pr => {
                if (pr.exercise && parseFloat(pr.weight) > 0) {
                    const wKg = (pr.unit && pr.unit.toUpperCase() === 'LBS') ? Math.round(parseFloat(pr.weight) * 0.453592 * 2) / 2 : parseFloat(pr.weight);
                    addPoint(pr.exercise, pr.date, wKg, 1, 10);
                }
            });
        }

        return map;
    }, [exerciseLogs, safeHistory, localPrs]);

    const availableLifts = useMemo(() => {
        const keys = Object.keys(allLoggedEntriesByLift);
        if (keys.length === 0) return ['deadlift', 'squat', 'bench press'];
        return keys.sort((a, b) => {
            const daysDiff = Object.keys(allLoggedEntriesByLift[b].pointsByDay).length - Object.keys(allLoggedEntriesByLift[a].pointsByDay).length;
            if (daysDiff !== 0) return daysDiff;
            return allLoggedEntriesByLift[b].totalSets - allLoggedEntriesByLift[a].totalSets;
        });
    }, [allLoggedEntriesByLift]);

    const [selectedLift, setSelectedLift] = useState(availableLifts[0] || 'deadlift');

    useEffect(() => {
        if (availableLifts && availableLifts.length > 0) {
            if (!availableLifts.includes(selectedLift.toLowerCase())) {
                setSelectedLift(availableLifts[0]);
            }
        }
    }, [availableLifts, selectedLift]);

    const selectBigLiftCategory = (bigLiftObj) => {
        const found = availableLifts.find(k => bigLiftObj.aliases.some(alias => k.includes(alias) || alias.includes(k)));
        if (found) setSelectedLift(found);
        else setSelectedLift(bigLiftObj.aliases[0]);
    };

    const liftProgressData = useMemo(() => {
        const key = selectedLift.toLowerCase().replace(/^barbell\s+/i, '');
        const entry = allLoggedEntriesByLift[key];
        if (!entry) return [];
        return Object.values(entry.pointsByDay)
            .sort((a, b) => a.dayIso.localeCompare(b.dayIso))
            .map(pt => ({
                date: pt.date,
                e1RM: pt.e1rm,
                Werkgewicht: pt.werkgewicht,
                reps: pt.reps,
                rpe: pt.rpe,
                bestSetText: pt.bestSetText
            }));
    }, [allLoggedEntriesByLift, selectedLift]);

    const liftSummaryMetrics = useMemo(() => {
        if (!liftProgressData || liftProgressData.length === 0) {
            return { bestE1RM: 0, maxWeight: 0, growthPct: 0, growthKg: 0, avgRpe: null };
        }
        const bestE1RM = Math.max(...liftProgressData.map(d => d.e1RM));
        const maxWeight = Math.max(...liftProgressData.map(d => d.Werkgewicht));
        const firstVal = liftProgressData[0].e1RM;
        const lastVal = liftProgressData[liftProgressData.length - 1].e1RM;
        const growthKg = lastVal - firstVal;
        const growthPct = firstVal > 0 ? Math.round(((lastVal - firstVal) / firstVal) * 100) : 0;

        const rpePoints = liftProgressData.filter(d => d.rpe !== null && d.rpe > 0);
        const avgRpe = rpePoints.length > 0
            ? (rpePoints.reduce((acc, c) => acc + c.rpe, 0) / rpePoints.length).toFixed(1)
            : null;

        return { bestE1RM, maxWeight, growthPct, growthKg, avgRpe };
    }, [liftProgressData]);

    const achievementData = [
        { id: 'workouts', name: 'Sessies Voltooid', icon: <Trophy size={22}/>, val: stats.allTime.workoutCount, tiers: [10, 50, 100, 250, 500, 1000], unit: 'WODs' },
        { id: 'tonnage', name: 'Volume Verplaatst', icon: <Dumbbell size={22}/>, val: stats.allTime.tonnage, tiers: [10000, 50000, 100000, 250000, 500000, 1000000], unit: 'kg' },
        { id: 'cals', name: 'Cardio Engine', icon: <Flame size={22}/>, val: stats.allTime.cals, tiers: [5000, 10000, 25000, 50000, 100000, 250000], unit: 'Punten' },
        { id: 'streak', name: 'Unstoppable Streak', icon: <Zap size={22}/>, val: currentStreak, tiers: [4, 12, 26, 52, 104, 208], unit: 'Weken' },
        { id: 'prs', name: 'Beast Mode (PRs)', icon: <Star size={22}/>, val: localPrs?.length || 0, tiers: [5, 10, 25, 50, 100, 200], unit: 'Records' },
        { id: 'hours', name: 'Time Lord', icon: <Clock size={22}/>, val: stats.allTime.time / 60, tiers: [10, 50, 100, 250, 500, 1000], unit: 'Uur' }
    ];

    const tierStyles = [
        { name: 'Nieuwkomer', color: 'text-zinc-500', bg: 'bg-zinc-900/60', glow: '' },
        { name: 'Brons', color: 'text-amber-600', bg: 'bg-amber-900/10', glow: 'shadow-[0_0_15px_rgba(217,119,6,0.1)]' },
        { name: 'Zilver', color: 'text-slate-300', bg: 'bg-slate-800/30', glow: 'shadow-[0_0_15px_rgba(203,213,225,0.15)]' },
        { name: 'Goud', color: 'text-yellow-400', bg: 'bg-yellow-900/20', glow: 'shadow-[0_0_15px_rgba(250,204,21,0.2)]' },
        { name: 'Platina', color: 'text-cyan-400', bg: 'bg-cyan-900/20', glow: 'shadow-[0_0_15px_rgba(34,211,238,0.2)]' },
        { name: 'Diamant', color: 'text-purple-400', bg: 'bg-purple-900/20', glow: 'shadow-[0_0_15px_rgba(192,132,252,0.2)]' },
        { name: 'Elite', color: 'text-orange-500', bg: 'bg-orange-900/30', glow: 'shadow-[0_0_20px_rgba(249,115,22,0.3)]' }
    ];

    const CompactTile = ({ icon, val, label, color }) => (
        <div className="bg-gradient-to-br from-white/10 to-black/50 p-3 sm:p-3.5 rounded-xl border border-orange-500/20 hover:border-orange-500/50 flex items-center gap-3 shadow-sm transition-all backdrop-blur-sm">
            <div className={`p-2.5 bg-black/40 rounded-xl border border-white/5 ${color} shrink-0 flex items-center justify-center`}>
                {icon}
            </div>
            <div className="min-w-0 text-left">
                <div className="text-base sm:text-lg font-black text-white leading-tight truncate">{val}</div>
                <div className="text-[10px] text-gray-400 uppercase tracking-wider font-bold truncate">{label}</div>
            </div>
        </div>
    );

    const CustomLiftTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length > 0) {
            const pt = payload[0].payload;
            return (
                <div className="bg-zinc-900/95 border border-orange-500/40 rounded-xl p-3 shadow-xl text-xs space-y-1">
                    <p className="font-black text-orange-500 uppercase tracking-wider">{label}</p>
                    <p className="text-white font-bold">Geschatte 1RM: <span className="text-orange-500">{pt.e1RM} kg</span></p>
                    <p className="text-gray-300">Beste Werkset: <span className="font-bold text-white">{pt.bestSetText}</span></p>
                    {pt.rpe && <p className="text-cyan-500 font-bold">Intensiteit: RPE {pt.rpe}</p>}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="space-y-6 pb-20">
            <div className="flex justify-center animate-fade-in">
                <div className="bg-black/50 p-1 rounded-xl flex flex-wrap justify-center gap-1 border border-orange-500/20 shadow-inner">
                    <button onClick={() => setStatsTab('personal')} className={`px-4 sm:px-6 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all flex items-center gap-2 ${statsTab === 'personal' ? 'bg-orange-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-200'}`}>
                        <Activity size={16}/> Mijn Prestaties
                    </button>
                    <button onClick={() => setStatsTab('achievements')} className={`px-4 sm:px-6 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all flex items-center gap-2 ${statsTab === 'achievements' ? 'bg-orange-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-200'}`}>
                        <Award size={16}/> Achievements
                    </button>
                    {safeHistory.some(w => {
                        const t = ((w.type || '') + ' ' + (w.description || '')).toLowerCase();
                        return t.includes('run:') || t.includes('duurloop') || t.includes('hardlopen') || t.includes('long run') || t.includes('richt-pace') || t.includes('streeftempo');
                    }) && (
                        <button onClick={() => setStatsTab('running')} className={`px-4 sm:px-6 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all flex items-center gap-2 ${statsTab === 'running' ? 'bg-orange-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-200'}`}>
                            <TrendingUp size={16}/> Running
                        </button>
                    )}
                    {leaderboard.active && (
                        <button onClick={() => setStatsTab('global')} className={`px-4 sm:px-6 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all flex items-center gap-2 ${statsTab === 'global' ? 'bg-orange-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-200'}`}>
                            <Globe size={16}/> Leaderboard
                        </button>
                    )}
                </div>
            </div>

            {statsTab === 'personal' && (
                <div className="animate-fade-in space-y-6">
                    <div className="flex justify-between items-center">
                        <h3 className="text-xs font-black uppercase tracking-widest text-gray-400">Overzicht & KPI's</h3>
                        <select
                            value={timeFilter}
                            onChange={e => setTimeFilter(e.target.value)}
                            className="bg-black/60 text-xs text-gray-200 font-bold border border-orange-500/30 rounded-lg px-3 py-2 outline-none focus:border-orange-500 cursor-pointer"
                        >
                            <option value="30">Laatste 30 Dagen</option>
                            <option value="90">Laatste 90 Dagen</option>
                            <option value="year">Dit Jaar</option>
                            <option value="all">All-Time Historie</option>
                        </select>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <CompactTile icon={<Trophy size={16}/>} val={stats.filtered.workoutCount} label="Trainingen" color="text-amber-500" />
                        <CompactTile icon={<Clock size={16}/>} val={`${(stats.filtered.time / 60).toFixed(1)}u`} label="Getraind" color="text-blue-500" />
                        <CompactTile icon={<Flame size={16}/>} val={stats.filtered.favFormat} label="Favoriet" color="text-orange-500" />
                        <CompactTile icon={<Zap size={16}/>} val={`${currentStreak} Wk`} label="Streak" color="text-emerald-500" />
                        <CompactTile icon={<Dumbbell size={16}/>} val={`${formatK(stats.filtered.tonnage)} kg`} label="Tonnage (Kracht)" color="text-orange-500" />
                        <CompactTile icon={<Activity size={16}/>} val={formatK(stats.filtered.cals)} label="Cardio Ptn" color="text-cyan-500" />
                        <CompactTile icon={<Calendar size={16}/>} val={`${avgSessies} / Wk`} label="Gemiddeld" color="text-purple-500" />
                        <CompactTile icon={<Star size={16}/>} val={stats.heaviestPR.weight !== undefined ? `${stats.heaviestPR.weight} ${stats.heaviestPR.unit.toLowerCase()}` : `${stats.heaviestPR} kg`} label="Zwaarste Lift" color="text-amber-500" />
                    </div>

                    <div className="w-full rounded-2xl border border-orange-500/20 overflow-hidden bg-black/40 shadow-lg p-4 sm:p-6">
                        <MuscleHeatmap user={user} history={safeHistory} exerciseDatabase={exerciseDatabase} serverFatigueData={fatigueData} />
                    </div>

                    <div className={`${glassClass} w-full p-4 sm:p-6 !border-orange-500/20`}>
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                            <div>
                                <h2 className="text-lg sm:text-xl font-black text-white uppercase tracking-wide flex items-center gap-2">
                                    <TrendingUp size={20} className="text-orange-500"/> Krachtprogressie & 1RM Analyse
                                </h2>
                                <p className="text-xs text-gray-400 mt-0.5">
                                    Automatisch berekend uit je gelogde werksets, herhalingen en RPE (Epley-formule).
                                </p>
                            </div>

                            <select
                                value={selectedLift}
                                onChange={(e) => setSelectedLift(e.target.value)}
                                className="w-full sm:w-auto bg-zinc-900 text-xs sm:text-sm text-white font-bold border border-zinc-700 rounded-xl px-3.5 py-2.5 outline-none focus:border-orange-500 capitalize"
                            >
                                {availableLifts.map(lift => {
                                    const count = Object.keys(allLoggedEntriesByLift[lift]?.pointsByDay || {}).length;
                                    return (
                                        <option key={lift} value={lift}>
                                            {lift} {count > 0 ? `(${count}x gelogd)` : ''}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>

                        <div className="flex flex-wrap gap-2 mb-5">
                            {BIG_LIFTS.map(bl => {
                                const isActive = bl.aliases.some(a => selectedLift.toLowerCase().includes(a));
                                return (
                                    <button
                                        key={bl.key}
                                        type="button"
                                        onClick={() => selectBigLiftCategory(bl)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${
                                            isActive
                                                ? 'bg-orange-600 text-white border-orange-500 shadow-sm'
                                                : 'bg-zinc-900/80 text-gray-400 border-zinc-700 hover:text-white'
                                        }`}
                                    >
                                        {bl.label}
                                    </button>
                                );
                            })}
                        </div>

                        {liftProgressData.length > 0 && (
                            <div className="grid grid-cols-3 gap-2.5 sm:gap-4 mb-5">
                                <div className="bg-black/40 border border-zinc-800 rounded-xl p-3 text-center">
                                    <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold block">Beste Geschatte 1RM</span>
                                    <span className="text-lg sm:text-2xl font-black text-orange-500">{liftSummaryMetrics.bestE1RM} <span className="text-xs">kg</span></span>
                                </div>
                                <div className="bg-black/40 border border-zinc-800 rounded-xl p-3 text-center">
                                    <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold block">Zwaarste Werkset</span>
                                    <span className="text-lg sm:text-2xl font-black text-white">{liftSummaryMetrics.maxWeight} <span className="text-xs text-gray-400">kg</span></span>
                                </div>
                                <div className="bg-black/40 border border-zinc-800 rounded-xl p-3 text-center">
                                    <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold block">
                                        {liftSummaryMetrics.avgRpe ? 'Gemiddelde RPE' : 'Progressie Trend'}
                                    </span>
                                    {liftSummaryMetrics.avgRpe ? (
                                        <span className="text-lg sm:text-2xl font-black text-cyan-500">RPE {liftSummaryMetrics.avgRpe}</span>
                                    ) : (
                                        <span className={`text-lg sm:text-2xl font-black ${liftSummaryMetrics.growthKg >= 0 ? 'text-emerald-500' : 'text-gray-300'}`}>
                                            {liftSummaryMetrics.growthKg >= 0 ? `+${liftSummaryMetrics.growthKg}kg` : `${liftSummaryMetrics.growthKg}kg`}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="w-full h-[250px] sm:h-[280px]">
                            {liftProgressData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart data={liftProgressData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="e1rmGrad" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#f97316" stopOpacity={0.35}/>
                                                <stop offset="95%" stopColor="#f97316" stopOpacity={0.0}/>
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#94a3b8" strokeOpacity={0.25} vertical={false} />
                                        <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickMargin={8} />
                                        <YAxis stroke="#64748b" fontSize={11} domain={['dataMin - 5', 'dataMax + 5']} allowDecimals={false} />
                                        <Tooltip content={<CustomLiftTooltip />} />
                                        <Area type="monotone" dataKey="e1RM" name="Geschatte 1RM" stroke="#f97316" strokeWidth={3} fill="url(#e1rmGrad)" dot={{ r: 4, fill: '#f97316', stroke: '#fff', strokeWidth: 1.5 }} activeDot={{ r: 6 }} />
                                        <Line type="monotone" dataKey="Werkgewicht" name="Zwaarste Werkset" stroke="#0284c7" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3, fill: '#0284c7' }} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex flex-col items-center justify-center h-full text-center p-6 bg-black/30 rounded-xl border border-zinc-800">
                                    <Dumbbell size={32} className="text-zinc-500 mb-2" />
                                    <p className="text-xs sm:text-sm text-gray-400">Nog geen gelogde sets gevonden voor <strong className="text-white capitalize">{selectedLift}</strong>.</p>
                                    <p className="text-[11px] text-gray-500 mt-1">Kies een andere oefening hierboven of vink je sets af tijdens een Krachttraining.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                        <div className="bg-black/40 p-4 sm:p-6 rounded-2xl border border-orange-500/20 shadow-inner flex flex-col items-center w-full min-h-[340px]">
                            <h3 className="text-sm font-black text-gray-300 mb-2 w-full text-left uppercase tracking-wider flex items-center gap-2"><Medal size={16} className="text-orange-500"/> MyWoD Atleet Profiel</h3>
                            <div className="w-full h-[220px] shrink-0">
                                <ResponsiveContainer width="100%" height="100%">
                                    <RadarChart cx="50%" cy="50%" outerRadius="72%" data={radarData}>
                                        <PolarGrid stroke="#94a3b8" strokeOpacity={0.35} />
                                        <PolarAngleAxis dataKey="subject" tick={{ fill: '#64748b', fontSize: 11, fontWeight: 'bold' }} />
                                        <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
                                        <Radar name="Score" dataKey="A" stroke="#f97316" fill="#ea580c" fillOpacity={0.4} />
                                        <Tooltip contentStyle={{ backgroundColor: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px', color: '#fff', fontSize: '12px' }} />
                                    </RadarChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="mt-2 w-full">
                                <div className="p-3.5 bg-zinc-800/30 border border-orange-500/20 rounded-xl text-left">
                                    <span className="font-black text-orange-500 flex items-center gap-2 mb-1 uppercase text-[11px] tracking-widest"><Target size={14}/> Coach Focus: {lowestCategory.subject}</span>
                                    <p className="text-gray-300 text-xs leading-relaxed">{coachingTips[lowestCategory.subject]}</p>
                                </div>
                            </div>
                        </div>

                        {/* Personal Records (1RM) Kaart met ✏️ Bewerk & LBS->KG Omreken-knop */}
                        <div className="bg-black/40 p-4 sm:p-6 rounded-2xl border border-orange-500/20 shadow-inner flex flex-col justify-between w-full min-h-[340px]">
                            <div>
                                <div className="flex justify-between items-center mb-4">
                                    <h2 className="text-sm font-black text-orange-500 uppercase tracking-wider flex items-center gap-2"><Star size={16} /> Personal Records (1RM)</h2>
                                    <span className="text-[11px] text-gray-400 font-bold">{localPrs?.length || 0} Records</span>
                                </div>
                                {Array.isArray(localPrs) && localPrs.length > 0 ? (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                                        {([...localPrs].sort((a, b) => parseFloat(b.weight) - parseFloat(a.weight)).slice(0, showAllPRs ? localPrs.length : 6)).map((pr, i) => (
                                            <div key={i} onClick={() => setSelectedLift(pr.exercise.toLowerCase())} className="cursor-pointer bg-zinc-900/80 p-3 pt-5 rounded-xl border border-zinc-800 hover:border-orange-500/50 transition-all text-center relative group">
                                                <div className="absolute top-1 right-1.5 flex items-center gap-1 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setEditPrModal({ isOpen: true, exercise: pr.exercise, weight: pr.weight, unit: (pr.unit || 'KG').toUpperCase(), syncLogs: true });
                                                        }}
                                                        className="text-gray-400 hover:text-orange-500 p-1"
                                                        title="Pas PR gewicht aan of reken LBS om naar KG"
                                                    >
                                                        <Edit3 size={12}/>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); deletePR(pr.exercise); }}
                                                        className="text-gray-400 hover:text-red-500 p-1"
                                                        title="Verwijder PR"
                                                    >
                                                        <Trash2 size={12}/>
                                                    </button>
                                                </div>
                                                <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider mb-1 truncate px-1" title={pr.exercise}>{pr.exercise}</p>
                                                <p className="text-lg font-black text-white leading-none">{pr.weight} <span className="text-[10px] text-orange-500 font-bold">{pr.unit ? pr.unit.toLowerCase() : 'kg'}</span></p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-xs text-gray-500 italic py-8 text-center">Geen handmatige 1RM records gevonden. Log een PR tijdens je workout!</p>
                                )}
                            </div>

                            {Array.isArray(localPrs) && localPrs.length > 6 && (
                                <div className="flex justify-center mt-4">
                                    <button onClick={() => setShowAllPRs(!showAllPRs)} className="text-xs font-bold text-orange-500 hover:text-orange-400 uppercase tracking-widest bg-orange-500/10 px-5 py-2 rounded-lg border border-orange-500/30 transition-colors">
                                        {showAllPRs ? 'Toon Minder' : `Toon Alle ${localPrs.length} Records`}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={`${glassClass} w-full p-4 sm:p-6 !border-orange-500/20`}>
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
                            <h2 className="text-lg sm:text-xl font-black text-orange-500 uppercase tracking-wide flex items-center gap-2"><Activity size={18} /> Wekelijkse Progressie</h2>
                            <div className="flex bg-black/50 p-1 rounded-lg border border-zinc-800">
                                <button
                                    type="button"
                                    onClick={() => setWeeklyChartMode('workouts')}
                                    className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${weeklyChartMode === 'workouts' ? 'bg-orange-600 text-white' : 'text-gray-400 hover:text-white'}`}
                                >
                                    Workouts & Duur
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setWeeklyChartMode('tonnage')}
                                    className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${weeklyChartMode === 'tonnage' ? 'bg-orange-600 text-white' : 'text-gray-400 hover:text-white'}`}
                                >
                                    Volume (Tonnage kg)
                                </button>
                            </div>
                        </div>

                        {chartData.length > 0 ? (
                            <div style={{ width: '100%', height: 250 }}>
                                <ResponsiveContainer width="100%" height={250}>
                                    {weeklyChartMode === 'workouts' ? (
                                        <ComposedChart data={chartData} margin={{ top: 5, right: 0, bottom: 5, left: -20 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#94a3b8" strokeOpacity={0.25} vertical={false} />
                                            <XAxis dataKey="week" stroke="#64748b" fontSize={10} />
                                            <YAxis yAxisId="left" stroke="#f97316" fontSize={10} allowDecimals={false} />
                                            <YAxis yAxisId="right" orientation="right" stroke="#eab308" fontSize={10} />
                                            <Tooltip contentStyle={{ backgroundColor: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px', color: '#fff', fontSize: '12px' }} />
                                            <Bar yAxisId="left" dataKey="Aantal" name="Workouts" fill="#f97316" radius={[4, 4, 0, 0]} maxBarSize={32} />
                                            <Line yAxisId="right" type="monotone" dataKey="Tijd" name="Totale Tijd (min)" stroke="#eab308" strokeWidth={3} activeDot={{ r: 6 }} />
                                        </ComposedChart>
                                    ) : (
                                        <ComposedChart data={chartData} margin={{ top: 5, right: 0, bottom: 5, left: -10 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#94a3b8" strokeOpacity={0.25} vertical={false} />
                                            <XAxis dataKey="week" stroke="#64748b" fontSize={10} />
                                            <YAxis stroke="#f97316" fontSize={10} allowDecimals={false} />
                                            <Tooltip contentStyle={{ backgroundColor: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px', color: '#fff', fontSize: '12px' }} />
                                            <Bar dataKey="Tonnage" name="Tonnage (kg)" fill="#ea580c" radius={[4, 4, 0, 0]} maxBarSize={36} />
                                        </ComposedChart>
                                    )}
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <p className="text-xs text-gray-500 italic">Doe eerst wat workouts binnen deze filterperiode!</p>
                        )}
                    </div>
                </div>
            )}

            {statsTab === 'achievements' && (
                <div className="animate-fade-in space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {achievementData.map(ach => {
                            let tLevel = 0;
                            for(let i = 0; i < ach.tiers.length; i++) {
                                if (ach.val >= ach.tiers[i]) tLevel = i + 1;
                            }
                            const style = tierStyles[tLevel];
                            const nextTier = tLevel < ach.tiers.length ? ach.tiers[tLevel] : ach.tiers[ach.tiers.length - 1];
                            const isMaxed = tLevel === ach.tiers.length;
                            const progress = isMaxed ? 100 : (ach.val / nextTier) * 100;

                            return (
                                <div key={ach.id} className={`${style.bg} border border-orange-500/20 p-5 rounded-2xl flex flex-col relative transition-all duration-300 hover:border-orange-500/50 hover:shadow-[0_4px_20px_rgba(249,115,22,0.1)] ${style.glow}`}>
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex items-center gap-4">
                                            <div className={`p-3 bg-black/50 rounded-xl border border-white/10 ${style.color} shadow-inner`}>
                                                {ach.icon}
                                            </div>
                                            <div>
                                                <h4 className="font-black text-white text-base sm:text-lg leading-tight uppercase tracking-wide">{ach.name}</h4>
                                                <div className={`text-[10px] uppercase tracking-widest font-bold mt-1 inline-block px-2 py-0.5 rounded bg-black/40 border border-white/5 ${style.color}`}>
                                                    {style.name}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-auto">
                                        <div className="flex justify-between items-end mb-2">
                                            <div className="font-black text-3xl text-white leading-none">{Math.floor(ach.val)} <span className="text-[10px] text-gray-400 uppercase font-bold tracking-widest ml-1">{ach.unit}</span></div>
                                            {!isMaxed && <div className="text-[10px] text-gray-400 uppercase font-bold tracking-widest">Doel: {nextTier}</div>}
                                        </div>

                                        {!isMaxed ? (
                                            <div className="w-full bg-black/60 border border-white/5 rounded-full h-3 shadow-inner overflow-hidden relative">
                                                <div className={`h-full rounded-full transition-all duration-1000 bg-gradient-to-r from-orange-600 to-yellow-400`} style={{width: `${progress}%`}}></div>
                                            </div>
                                        ) : (
                                            <div className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white text-[10px] font-black uppercase tracking-widest py-1.5 text-center rounded-lg shadow-lg border border-orange-500/50">
                                                Maximum Level Bereikt
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {statsTab === 'global' && leaderboard.active && (
                <div className="animate-fade-in space-y-8">
                    <div className="flex justify-center">
                        <span className="bg-zinc-900 text-orange-500 border border-orange-500/30 px-6 py-2 rounded-full text-sm font-black uppercase tracking-widest flex items-center gap-2 shadow-lg">
                            <Trophy size={16}/> {isFitness ? '1RM Hall of Fame' : 'Benchmark WODs'}
                        </span>
                    </div>

                    {isFitness ? (
                        Object.keys(groupedLifts).length === 0 ? (
                            <div className="bg-black/30 p-12 rounded-2xl border border-orange-500/20 text-center text-gray-500">
                                <Dumbbell size={48} className="mx-auto mb-4 opacity-30" />
                                Er zijn nog geen zware 1RM records gelogd.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                                {Object.entries(groupedLifts).map(([exercise, scores]) => (
                                    <div key={exercise} className="bg-[#141414] border-y border-r border-orange-500/20 border-l-4 border-l-orange-500 rounded-r-2xl rounded-l-sm shadow-xl flex flex-col h-full hover:border-orange-500/50 transition-colors">
                                        <div className="mb-2 pb-2 border-b border-orange-500/20 p-4 sm:p-6 pb-0">
                                            <h3 className="text-lg font-black text-orange-500 tracking-widest uppercase">{exercise}</h3>
                                        </div>
                                        <div className="p-4 sm:p-6 pt-0 space-y-2 flex-1">
                                            {scores.map((score, idx) => (
                                                <div key={idx} className={`flex justify-between items-center p-3 rounded-xl border transition-all ${idx === 0 ? 'bg-gradient-to-r from-yellow-900/20 to-[#1a1a1a] border-yellow-500/50 shadow-[0_0_15px_rgba(234,179,8,0.15)]' : idx === 1 ? 'bg-gradient-to-r from-gray-500/10 to-[#1a1a1a] border-gray-400/30' : idx === 2 ? 'bg-gradient-to-r from-orange-900/20 to-[#1a1a1a] border-orange-700/30' : 'bg-[#1e1e1e] border-white/5'}`}>
                                                    <div className="flex items-center gap-4">
                                                        <span className={`font-black text-xl w-6 text-center ${idx === 0 ? 'text-yellow-500 drop-shadow-md' : idx === 1 ? 'text-gray-400' : idx === 2 ? 'text-orange-500' : 'text-gray-600'}`}>#{idx + 1}</span>
                                                        <div>
                                                            <div className={`font-bold ${score.name === 'Anonieme Atleet' ? 'text-gray-500 italic' : 'text-white'}`}>{score.name}</div>
                                                            <div className="text-[10px] text-gray-500 uppercase tracking-widest">{new Date(score.date).toLocaleDateString('nl-NL')}</div>
                                                        </div>
                                                    </div>
                                                    <div className={`font-black text-2xl ${idx === 0 ? 'text-yellow-500' : 'text-white'}`}>
                                                        {score.weight} <span className="text-sm text-gray-400">{score.unit ? score.unit.toLowerCase() : 'kg'}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    ) : (
                        Object.keys(groupedLeaderboard).length === 0 ? (
                            <div className="bg-black/30 p-12 rounded-2xl border border-orange-500/20 text-center text-gray-500">
                                <Activity size={48} className="mx-auto mb-4 opacity-30" />
                                Er zijn nog geen scores voor Hero of Benchmark WODs gelogd. Start een Benchmark!
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                                {Object.entries(groupedLeaderboard).map(([wodName, scores]) => (
                                    <div key={wodName} className="bg-[#141414] border-y border-r border-orange-500/20 border-l-4 border-l-orange-500 rounded-r-2xl rounded-l-sm shadow-xl flex flex-col h-full hover:border-orange-500/50 transition-colors">
                                        <div className="mb-2 pb-2 border-b border-orange-500/20 p-4 sm:p-6 pb-0">
                                            <h3 className="text-lg font-black text-orange-500 tracking-widest uppercase">{wodName}</h3>
                                        </div>
                                        <div className="p-4 sm:p-6 pt-0 space-y-2 flex-1">
                                            {scores.map((score, idx) => (
                                                <div key={idx} className={`flex justify-between items-center p-3 rounded-xl border transition-all ${idx === 0 ? 'bg-gradient-to-r from-yellow-900/20 to-[#1a1a1a] border-yellow-500/50 shadow-[0_0_15px_rgba(234,179,8,0.15)]' : idx === 1 ? 'bg-gradient-to-r from-gray-500/10 to-[#1a1a1a] border-gray-400/30' : idx === 2 ? 'bg-gradient-to-r from-orange-900/20 to-[#1a1a1a] border-orange-700/30' : 'bg-[#1e1e1e] border-white/5'}`}>
                                                    <div className="flex items-center gap-4">
                                                        <span className={`font-black text-xl w-6 text-center ${idx === 0 ? 'text-yellow-500 drop-shadow-md' : idx === 1 ? 'text-gray-400' : idx === 2 ? 'text-orange-500' : 'text-gray-600'}`}>#{idx + 1}</span>
                                                        <div>
                                                            <div className={`font-bold ${score.name === 'Anonieme Atleet' ? 'text-gray-500 italic' : 'text-white'}`}>{score.name}</div>
                                                            <div className="text-[10px] text-gray-500 uppercase tracking-widest">{new Date(score.date).toLocaleDateString('nl-NL')}</div>
                                                        </div>
                                                    </div>
                                                    <div className={`font-black text-xl ${idx === 0 ? 'text-yellow-500' : 'text-white'}`}>
                                                        {score.result}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    )}
                </div>
            )}

            {/* MODAL VOOR BEWERKEN OF LBS->KG OMREKENEN VAN PERSONAL RECORDS */}
            {editPrModal.isOpen && (
                <div className="fixed inset-0 z-[99999] bg-black/80 backdrop-blur-sm flex justify-center items-center p-4">
                    <div className="bg-zinc-900 p-6 sm:p-8 rounded-2xl w-full max-w-sm border border-zinc-700 shadow-2xl">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-black text-orange-500 flex items-center gap-2"><Star size={20}/> PR Aanpassen</h3>
                            <button onClick={() => setEditPrModal({ isOpen: false, exercise: '', weight: '', unit: 'KG', syncLogs: true })} className="text-gray-400 hover:text-white p-1 rounded-full"><X size={18}/></button>
                        </div>
                        <p className="text-xs text-gray-400 mb-4">Pas het 1RM record aan voor <strong className="text-white">{editPrModal.exercise}</strong>:</p>

                        <form onSubmit={handleSavePrEdit} className="space-y-4">
                            <div className="flex gap-2">
                                <input
                                    type="number"
                                    step="any"
                                    value={editPrModal.weight}
                                    onChange={(e) => setEditPrModal(prev => ({ ...prev, weight: e.target.value }))}
                                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl p-3.5 text-center text-2xl font-black text-white outline-none focus:border-orange-500"
                                    autoFocus
                                />
                                <select
                                    value={editPrModal.unit}
                                    onChange={(e) => setEditPrModal(prev => ({ ...prev, unit: e.target.value }))}
                                    className="bg-zinc-800 border border-zinc-700 rounded-xl px-4 text-sm font-black text-orange-500 outline-none"
                                >
                                    <option value="KG">KG</option>
                                    <option value="LBS">LBS</option>
                                </select>
                            </div>

                            <button
                                type="button"
                                onClick={convertPrLbsToKg}
                                className="w-full py-2.5 px-3 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-500/30 rounded-xl text-xs font-black uppercase tracking-wider transition-all"
                            >
                                🔄 Reken huidige waarde om van LBS → KG (÷ 2.2)
                            </button>

                            <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer pt-1">
                                <input
                                    type="checkbox"
                                    checked={editPrModal.syncLogs}
                                    onChange={(e) => setEditPrModal(prev => ({ ...prev, syncLogs: e.target.checked }))}
                                    className="w-4 h-4 accent-orange-500 rounded"
                                />
                                <span>Corrigeer ook te hoge uitschieters in 1RM-grafiek</span>
                            </label>

                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={() => setEditPrModal({ isOpen: false, exercise: '', weight: '', unit: 'KG', syncLogs: true })} className="flex-1 py-3 bg-zinc-800 text-gray-300 font-bold rounded-xl text-xs uppercase">Annuleren</button>
                                <button type="submit" className="flex-1 py-3 bg-orange-600 hover:bg-orange-500 text-white font-black rounded-xl text-xs uppercase">Opslaan</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
